import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AtSignIcon, LockIcon, UserIcon, AppleIcon, LinkedinIcon, XIcon, EyeIcon, EyeOffIcon, CodeIcon, TerminalIcon } from 'lucide-react';
interface SignupProps {
  onSignup: (userType: 'mentor' | 'mentee', skipProfileSetup?: boolean) => void;
}
interface ErrorNotification {
  message: string;
  show: boolean;
}
const Signup: React.FC<SignupProps> = ({
  onSignup
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userTypeSelection, setUserTypeSelection] = useState<'mentor' | 'mentee'>('mentee');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [error, setError] = useState<ErrorNotification>({
    message: '',
    show: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showDevLoginModal, setShowDevLoginModal] = useState(false);
  const showError = (message: string) => {
    setError({
      message,
      show: true
    });
    setTimeout(() => setError({
      message: '',
      show: false
    }), 3000);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showError('Please enter a valid email address');
      return;
    }
    if (password.length < 6) {
      showError('Password must be at least 6 characters long');
      return;
    }
    if (password !== confirmPassword) {
      showError('Passwords do not match');
      return;
    }
    if (!agreeToTerms) {
      showError('You must agree to the terms and privacy policy');
      return;
    }
    const userData = {
      email,
      userType: userTypeSelection,
      profileComplete: false,
      isTestAccount: email === 'trenty@test.com' && password === 'test!234'
    };
    localStorage.setItem('userData', JSON.stringify(userData));
    onSignup(userTypeSelection);
  };
  // Developer login flow handler
  const handleDevLogin = (type: 'mentor' | 'mentee', skipProfile: boolean) => {
    const userData = {
      email: `dev-${type}@pridementor.dev`,
      userType: type,
      profileComplete: skipProfile
    };
    localStorage.setItem('userData', JSON.stringify(userData));
    // Pass skipProfile parameter to onSignup to properly handle dashboard access
    onSignup(type, skipProfile);
    setShowDevLoginModal(false);
  };
  const DevLoginModal = () => <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-281">
      <div className="bg-white rounded-xl w-full max-w-md" data-id="element-282">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-283">
          <div className="flex items-center" data-id="element-284">
            <TerminalIcon size={18} className="text-purple-600 mr-2" data-id="element-285" />
            <h3 className="font-semibold" data-id="element-286">Developer Login</h3>
          </div>
          <button onClick={() => setShowDevLoginModal(false)} data-id="element-287">
            <XIcon size={20} data-id="element-288" />
          </button>
        </div>
        <div className="p-6" data-id="element-289">
          <p className="text-gray-600 text-sm mb-4" data-id="element-290">
            Select a user flow to quickly access without credentials:
          </p>
          <div className="space-y-3" data-id="element-291">
            <button onClick={() => handleDevLogin('mentee', false)} className="w-full text-left px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 flex items-center" data-id="element-292">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3" data-id="element-293">
                <UserIcon size={16} className="text-blue-600" data-id="element-294" />
              </div>
              <div data-id="element-295">
                <div className="font-medium" data-id="element-296">Mentee Signup Flow</div>
                <div className="text-xs text-gray-500" data-id="element-297">
                  Complete profile setup as a mentee
                </div>
              </div>
            </button>
            <button onClick={() => handleDevLogin('mentor', false)} className="w-full text-left px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 flex items-center" data-id="element-298">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3" data-id="element-299">
                <UserIcon size={16} className="text-purple-600" data-id="element-300" />
              </div>
              <div data-id="element-301">
                <div className="font-medium" data-id="element-302">Mentor Signup Flow</div>
                <div className="text-xs text-gray-500" data-id="element-303">
                  Complete profile setup as a mentor
                </div>
              </div>
            </button>
            <button onClick={() => handleDevLogin('mentee', true)} className="w-full text-left px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 flex items-center" data-id="element-304">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3" data-id="element-305">
                <UserIcon size={16} className="text-green-600" data-id="element-306" />
              </div>
              <div data-id="element-307">
                <div className="font-medium" data-id="element-308">Mentee Dashboard</div>
                <div className="text-xs text-gray-500" data-id="element-309">
                  Skip to mentee dashboard
                </div>
              </div>
            </button>
            <button onClick={() => handleDevLogin('mentor', true)} className="w-full text-left px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 flex items-center" data-id="element-310">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3" data-id="element-311">
                <UserIcon size={16} className="text-yellow-600" data-id="element-312" />
              </div>
              <div data-id="element-313">
                <div className="font-medium" data-id="element-314">Mentor Dashboard</div>
                <div className="text-xs text-gray-500" data-id="element-315">
                  Skip to mentor dashboard
                </div>
              </div>
            </button>
          </div>
          <div className="mt-6 text-center" data-id="element-316">
            <button onClick={() => setShowDevLoginModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" data-id="element-317">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>;
  return <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-600 to-purple-800 p-6" data-id="element-318">
      {error.show && <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-md z-50 flex items-center justify-between min-w-[300px]" data-id="element-319">
          <span data-id="element-320">{error.message}</span>
          <button onClick={() => setError({
        message: '',
        show: false
      })} className="ml-4" data-id="element-321">
            <XIcon size={16} data-id="element-322" />
          </button>
        </div>}
      <div className="flex justify-center mb-6 mt-8" data-id="element-323">
        <div className="flex flex-col items-center" data-id="element-324">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-3" data-id="element-325">
            <div className="text-purple-600 font-bold text-3xl" data-id="element-326">PM</div>
          </div>
          <h1 className="text-white text-2xl font-bold" data-id="element-327">PrideMentor</h1>
          <p className="text-purple-200 text-sm" data-id="element-328">Connect. Grow. Thrive.</p>
        </div>
      </div>
      <div className="bg-white rounded-xl p-6 shadow-lg" data-id="element-329">
        <h2 className="text-xl font-bold text-center mb-6" data-id="element-330">Create Account</h2>
        <div className="flex justify-center space-x-4 mb-6" data-id="element-331">
          <button type="button" className={`px-4 py-2 rounded-full ${userTypeSelection === 'mentee' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setUserTypeSelection('mentee')} data-id="element-332">
            I'm a Mentee
          </button>
          <button type="button" className={`px-4 py-2 rounded-full ${userTypeSelection === 'mentor' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setUserTypeSelection('mentor')} data-id="element-333">
            I'm a Mentor
          </button>
        </div>
        <form onSubmit={handleSubmit} data-id="element-334">
          <div className="mb-4" data-id="element-335">
            <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-336">
              Email
            </label>
            <div className="relative" data-id="element-337">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-338">
                <AtSignIcon size={18} className="text-gray-400" data-id="element-339" />
              </div>
              <input type="email" className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} required data-id="element-340" />
            </div>
          </div>
          <div className="mb-4" data-id="element-341">
            <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-342">
              Password
            </label>
            <div className="relative" data-id="element-343">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-344">
                <LockIcon size={18} className="text-gray-400" data-id="element-345" />
              </div>
              <input type={showPassword ? 'text' : 'password'} className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} data-id="element-346" />
              <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)} data-id="element-347">
                {showPassword ? <EyeOffIcon size={18} className="text-gray-400" data-id="element-348" /> : <EyeIcon size={18} className="text-gray-400" data-id="element-349" />}
              </button>
            </div>
          </div>
          <div className="mb-6" data-id="element-350">
            <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-351">
              Confirm Password
            </label>
            <div className="relative" data-id="element-352">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-353">
                <LockIcon size={18} className="text-gray-400" data-id="element-354" />
              </div>
              <input type={showConfirmPassword ? 'text' : 'password'} className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required data-id="element-355" />
              <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowConfirmPassword(!showConfirmPassword)} data-id="element-356">
                {showConfirmPassword ? <EyeOffIcon size={18} className="text-gray-400" data-id="element-357" /> : <EyeIcon size={18} className="text-gray-400" data-id="element-358" />}
              </button>
            </div>
          </div>
          <div className="mb-6" data-id="element-359">
            <label className="flex items-center" data-id="element-360">
              <input type="checkbox" className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" checked={agreeToTerms} onChange={e => setAgreeToTerms(e.target.checked)} required data-id="element-361" />
              <span className="ml-2 text-sm text-gray-600" data-id="element-362">
                I agree to the{' '}
                <a href="#" className="text-purple-600 hover:underline" data-id="element-363">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="#" className="text-purple-600 hover:underline" data-id="element-364">
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>
          <button type="submit" className="w-full bg-purple-600 text-white py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors" data-id="element-365">
            Create Account
          </button>
        </form>
        <div className="my-6 flex items-center" data-id="element-366">
          <div className="flex-1 border-t border-gray-300" data-id="element-367"></div>
          <span className="px-3 text-gray-500 text-sm" data-id="element-368">or sign up with</span>
          <div className="flex-1 border-t border-gray-300" data-id="element-369"></div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-6" data-id="element-370">
          <button className="flex justify-center items-center py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-371">
            <UserIcon size={18} className="text-red-500" data-id="element-372" />
          </button>
          <button className="flex justify-center items-center py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-373">
            <AppleIcon size={18} data-id="element-374" />
          </button>
          <button className="flex justify-center items-center py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-375">
            <LinkedinIcon size={18} className="text-blue-600" data-id="element-376" />
          </button>
        </div>
        <p className="text-center text-gray-600 text-sm" data-id="element-377">
          Already have an account?{' '}
          <Link to="/login" className="text-purple-600 font-medium hover:underline" data-id="element-378">
            Sign in
          </Link>
        </p>
      </div>
      {/* Developer Login Link */}
      <div className="mt-6 text-center" data-id="element-379">
        <button onClick={() => setShowDevLoginModal(true)} className="text-xs text-purple-200 hover:text-white flex items-center justify-center mx-auto opacity-70 hover:opacity-100" data-id="element-380">
          <CodeIcon size={14} className="mr-1" data-id="element-381" />
          Developer Login
        </button>
      </div>
      {/* Dev Login Modal */}
      {showDevLoginModal && <DevLoginModal data-id="element-382" />}
    </div>;
};
export default Signup;